import {
  useGetAIProvider,
  useGetTenantConfigurations,
  useUpdateTenantConfigurations
} from "@/api/useTenant"
import { Flex, Skeleton, Text } from "@/atoms"
import { isSelfHosted } from "@/helpers/self-hosted.helper"
import { useTranslation } from "@/hooks/useTranslation"
import { useUser } from "@/hooks/useUser"
import { Heading } from "@/molecules/Heading"
import { Form } from "@/organisms/Form"
import { isAIProviderValid } from "@/pages/SettingsPage/helpers/aiProvider"
import { VerticalTabTemplate } from "../../templates/VerticalTabTemplate"
import { AIProviderCard } from "../AIProviderCard"
import { FormKaiTab } from "./FormKaiTab"

import styles from "./KaiTab.module.scss"

export const KaiTab = () => {
  const { t } = useTranslation("translation", { keyPrefix: "settings.kai" })
  const { t: tGeneral } = useTranslation()
  const { response, isLoading } = useGetTenantConfigurations()
  const { mutateAsync: createTenantConfigurations, isPending } =
    useUpdateTenantConfigurations()
  const { data } = response || {}
  const kaiSastLlm = data?.kaiSastLlm || false
  // Self-hosted Kai runs on the customer's own AI vendor; cloud has no such setting.
  const { hasPermission } = useUser()
  const canConfigureAIProvider =
    isSelfHosted() && !!hasPermission({ resource: "system", action: "write" })
  const aiProvider = useGetAIProvider(canConfigureAIProvider)
  const provider = aiProvider.response?.data
  const kaiNeedsAIProvider = isSelfHosted() && !isAIProviderValid(provider)
  const aiProviderMissing =
    kaiNeedsAIProvider && !aiProvider.isLoading && !aiProvider.isError

  return (
    <Form.Page
      id="kai-tab"
      Component={VerticalTabTemplate}
      title={<Heading level={4} title={t("title")} />}
      footer={
        <Form.Footer
          submitting={isPending}
          okText={() => tGeneral("general.apply")}
        />
      }
    >
      <Flex vertical>
        <Text type="secondary" className="sm-text-ks">
          {t("description1")}
        </Text>
        <Text type="secondary" className="sm-text-ks">
          {t("description2")}
        </Text>
      </Flex>
      <div className={styles.formWrapper}>
        <Flex vertical gap={12}>
          {canConfigureAIProvider && (
            <AIProviderCard
              provider={provider}
              isLoading={aiProvider.isLoading}
              isError={aiProvider.isError && !aiProvider.isFetching}
            />
          )}
          {isLoading && <Skeleton active paragraph={{ rows: 1 }} />}
          {!isLoading && (
            <FormKaiTab
              key={kaiSastLlm ? "enabled" : "disabled"}
              initialValues={data}
              disabled={kaiNeedsAIProvider}
              showAIProviderAlert={aiProviderMissing}
              aiProviderConfigured={provider?.configured}
              createTenantConfigurations={createTenantConfigurations}
              t={t}
            />
          )}
        </Flex>
      </div>
    </Form.Page>
  )
}
